import React, { useRef, useLayoutEffect } from 'react';
import { segmentsFromMarks } from '../../../../lib/richEditor/richText';

/**
 * A single block's editable line: a real (but glyph-transparent) <textarea>
 * handles all typing/selection/caret placement, stacked over a read-only
 * styled overlay that renders the exact same plain string with marks
 * applied. Because the buffer is plain text — no markdown tokens, no
 * hidden markers — both layers render identical characters at identical
 * widths, so there's no reserved gap and the invisible caret lines up
 * exactly with the visible formatted run underneath it.
 *
 * A <textarea>, not an <input>: the overlay wraps long text onto multiple
 * visual lines (`whitespace-pre-wrap break-words`), and only a <textarea>
 * can wrap to match it — a single-line <input> stays on one line and
 * scrolls horizontally, so past the first wrapped line its invisible glyph
 * positions no longer line up with what's on screen and drag-selection
 * breaks. Auto-grows to its content height (below) so it never shows its
 * own internal scrollbar.
 *
 * DOM port of teacher-mobile's RichLine.tsx (same trick, no contenteditable).
 */
export default function RichLine({
  text, marks, placeholder, blockClassName,
  onChangeText, onSelectionChange, onFocus, onBlur, onKeyDown, onPaste, inputRef,
}) {
  const localRef = useRef(null);

  useLayoutEffect(() => {
    inputRef(localRef.current);
    return () => inputRef(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const el = localRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text, blockClassName]);

  const segments = segmentsFromMarks(text, marks);

  return (
    <div className="relative flex-1">
      <div className={`${blockClassName} whitespace-pre-wrap break-words py-[3px] pointer-events-none`} aria-hidden="true">
        {text
          ? segments.map((seg, i) => <span key={i} style={seg.style}>{seg.text}</span>)
          : <span className="text-gray-300">{placeholder}</span>}
      </div>
      <textarea
        ref={localRef}
        value={text}
        rows={1}
        onChange={(e) => onChangeText(e.target.value)}
        onSelect={(e) => onSelectionChange({ start: e.target.selectionStart, end: e.target.selectionEnd })}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        className={`${blockClassName} absolute inset-0 top-0 left-0 w-full h-auto p-0 py-[3px] bg-transparent border-none outline-none resize-none overflow-hidden whitespace-pre-wrap break-words`}
        style={{ color: 'transparent', caretColor: '#111827' }}
        data-testid="rich-line-input"
      />
    </div>
  );
}
